import { useState, useCallback } from 'react';
import api from '@/services/api';

/**
 * Hook to manage download gate logic
 * Shows modal only once per profile, then allows direct downloads
 */
export function useDownloadGate(profileId) {
  const [showModal, setShowModal] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  /**
   * Check if lead was already captured
   */
  const checkLeadStatus = useCallback(async () => {
    if (!profileId) return { captured: false };
    
    try {
      const res = await api.get(`/leads/status/${profileId}`);
      return res.data;
    } catch (error) {
      console.error('Lead status check failed:', error);
      return { captured: false };
    }
  }, [profileId]);

  /**
   * Initiate download - checks lead status first
   * @param downloadFn - Function to execute the actual download
   * @param downloadType - Type of download (cv_pdf, analysis, personality_report)
   */
  const initiateDownload = useCallback(async (downloadFn, downloadType = 'cv_pdf') => {
    if (!profileId) {
      // No profile, just download
      downloadFn?.();
      return;
    }

    setIsChecking(true);
    
    try {
      const status = await checkLeadStatus();
      
      if (status.captured) {
        // Already captured, proceed with download
        downloadFn?.();
      } else {
        // Need to capture lead first
        setPendingDownload({ fn: downloadFn, type: downloadType });
        setShowModal(true);
      }
    } catch (error) {
      console.error('Download gate error:', error);
      // On error, proceed with download anyway
      downloadFn?.();
    } finally {
      setIsChecking(false);
    }
  }, [profileId, checkLeadStatus]);

  /**
   * Called when lead is successfully captured
   */
  const onLeadCaptured = useCallback(() => {
    // Execute pending download
    if (pendingDownload?.fn) {
      pendingDownload.fn();
    }
    setPendingDownload(null);
    setShowModal(false);
  }, [pendingDownload]);

  /**
   * Close modal without capturing
   */
  const closeModal = useCallback(() => {
    setPendingDownload(null);
    setShowModal(false);
  }, []);

  return {
    showModal,
    setShowModal,
    pendingDownload,
    isChecking,
    initiateDownload,
    onLeadCaptured,
    closeModal,
    downloadType: pendingDownload?.type || 'cv_pdf'
  };
}

export default useDownloadGate;
